import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { createEventHandler } from '@wix/tpa-settings';
import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import { ButtonDemo, IEvents, PUBLISH_EVENT_TIMEOUT, SETTINGS_EVENTS, TabState } from '../../constants/settings-events';
import { plansFixtureTranslated } from '../../fixtures';
import { createPlansApi, memoizePlansApi } from '../../services';
import { Analytics } from '../../services/analytics';
import { commonBIDataFromFlowAPI } from '../../services/commonBIData';
import { WarmupData } from '../../services/WarmupData';
import { PlanListInteractions } from '../../types/PlanListFedops';
import { isViewerOnEcom } from '../../utils/ecom';
import { isMultiPageApp } from '../../utils/isMultiPageApp';
import { Navigation } from '../PackagePicker/controller/Navigation';
import { SettingsReader } from '../PackagePicker/DefaultSettingsAdapter';
import { PlanListController } from './PlanListController';
import settingsParams from './settingsParams';

const createController: CreateControllerFn = async (params) => {
  const {
    flowAPI,
    controllerConfig: { setProps, wixCodeApi, appParams, config, compId },
  } = params;

  const defaultDemoPlans = plansFixtureTranslated(flowAPI.translations.t);

  const demoPlans = config.publicData?.APP?.demoData?.plans ?? defaultDemoPlans;

  flowAPI.bi?.updateDefaults(commonBIDataFromFlowAPI(flowAPI));
  const api = createPlansApi(flowAPI);
  const componentEventHandler = createEventHandler<IEvents>(config.publicData.COMPONENT || {});
  let settingsResetTimeoutId: NodeJS.Timeout | undefined;
  const isEcomEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.USE_SERVERLESS_FOR_EIE)
    ? await isViewerOnEcom(flowAPI.httpClient, wixCodeApi, appParams, flowAPI.errorHandler)
    : flowAPI.experiments.enabled(TPA_EXPERIMENTS.ECOM_INTEGRATION_ENABLED);
  const analytics = new Analytics(wixCodeApi.window);
  const settings = new SettingsReader(flowAPI.settings, settingsParams);
  const warmupData = new WarmupData(compId, wixCodeApi, flowAPI);
  const navigation = new Navigation(flowAPI, appParams.appDefinitionId, await isMultiPageApp(wixCodeApi, appParams));
  const pp = new PlanListController(
    setProps,
    memoizePlansApi(api),
    flowAPI,
    wixCodeApi,
    appParams,
    analytics,
    settings,
    demoPlans,
    warmupData,
    navigation,
    isEcomEnabled,
  );

  function resetSettingsState() {
    pp.setProps({ tabState: TabState.REGULAR, areSettingsOpened: false, buttonDemo: ButtonDemo.NONE });
  }

  function delaySettingsStateReset() {
    if (!flowAPI.experiments.enabled(TPA_EXPERIMENTS.SETTINGS_EVENTS_FIX)) {
      return;
    }

    if (settingsResetTimeoutId) {
      clearTimeout(settingsResetTimeoutId);
    }

    settingsResetTimeoutId = setTimeout(() => resetSettingsState(), PUBLISH_EVENT_TIMEOUT * 2);
  }

  return {
    async pageReady() {
      flowAPI.panoramaClient?.transaction(PlanListInteractions.PageReady).start();

      setProps({ fitToContentHeight: true, isOnEcom: isEcomEnabled });

      componentEventHandler.on(SETTINGS_EVENTS.TAB_STATE, (tabState: TabState) => {
        pp.setProps({ tabState });
        delaySettingsStateReset();
      });

      componentEventHandler.on(SETTINGS_EVENTS.ARE_SETTINGS_OPENED, (areSettingsOpened: boolean) => {
        pp.setProps({ areSettingsOpened });
        delaySettingsStateReset();
      });

      if (flowAPI.experiments.enabled(TPA_EXPERIMENTS.BUTTON_HOVER_SETTINGS_EDITOR)) {
        componentEventHandler.on(SETTINGS_EVENTS.BUTTON_DEMO, (buttonDemo: ButtonDemo) => {
          pp.setProps({ buttonDemo });
          delaySettingsStateReset();
        });
      }

      componentEventHandler.onReset(() => resetSettingsState());

      flowAPI.panoramaClient?.transaction(PlanListInteractions.PageReady).finish();
      await pp.initialize();
    },
    async updateConfig(_$w, newConfig) {
      componentEventHandler.notify(newConfig.publicData.COMPONENT || {});
      setProps(await pp.fetchAndOrderPlans());
    },
  };
};

export default createController;
